import useTitanApp from "@/hooks/useTitanApp";
import {
  addPatientMedications,
  getPatientMedications,
} from "@/dispatchers/patientDispatchers";
import { usePagination } from "@/components/ui/table-pagination";

interface MedicationTimes {
  morning: string;
  midday: string;
  evening: string;
  night: string;
}

export interface Medication {
  id: string;
  item: string;
  dose: string;
  times: MedicationTimes;
  quantity: string;
  measure: string;
  therapyLevel: "acute" | "chronic" | "preventive";
  preparation: string;
  notes: string;
}

export const defaultMedications: Medication[] = [
  {
    id: "1",
    item: "Amlodipine 10mg tablets",
    dose: "Take One daily",
    times: {
      morning: "1",
      midday: "0",
      evening: "0",
      night: "0",
    },
    quantity: "1",
    measure: "tablet",
    therapyLevel: "chronic",
    preparation: "tablet",
    notes: "For blood pressure control",
  },
  {
    id: "2",
    item: "Metformin 500mg tablets",
    dose: "Take Two daily",
    times: {
      morning: "1",
      midday: "0",
      evening: "1",
      night: "0",
    },
    quantity: "2",
    measure: "tablet",
    therapyLevel: "chronic",
    preparation: "tablet",
    notes: "For diabetes management",
  },
];

export const therapyLevelOptions = [
  { value: "acute", label: "Acute", color: "red" },
  { value: "chronic", label: "Chronic", color: "orange" },
  { value: "preventive", label: "Preventive", color: "blue" },
] as const;

export const measureOptions = [
  "tablet",
  "capsule",
  "ml",
  "mg",
  "g",
  "drops",
  "puffs",
  "units",
  "patches",
  "sachets",
];

export const preparationOptions = [
  "tablet",
  "capsule",
  "liquid",
  "injection",
  "inhaler",
  "cream",
  "ointment",
  "gel",
  "patch",
  "suppository",
  "drops",
  "spray",
];

export const commonMedications = [
  "Amlodipine",
  "Metformin",
  "Simvastatin",
  "Omeprazole",
  "Ramipril",
  "Bisoprolol",
  "Atorvastatin",
  "Paracetamol",
  "Ibuprofen",
  "Aspirin",
  "Warfarin",
  "Lansoprazole",
  "Bendroflumethiazide",
  "Salbutamol",
  "Levothyroxine",
];

const mapTherapyLevel = (level: number): "acute" | "chronic" | "preventive" => {
  switch (level) {
    case 1:
      return "acute";
    case 2:
      return "chronic";
    case 3:
      return "preventive";
    default:
      return "acute";
  }
};

const mapTherapyLevelToNumber = (
  level: "acute" | "chronic" | "preventive",
): number => {
  switch (level) {
    case "acute":
      return 1;
    case "chronic":
      return 2;
    case "preventive":
      return 3;
    default:
      return 1;
  }
};

export const useMedicationsState = () => {
  const { patientState } = useTitanApp();
  const {
    patientMedications,
    patientMedicationsLoading,
    selectedPatientDetails,
    consultationId,
  } = patientState || {};
  const { patientID } = selectedPatientDetails || {};

  const DashboardPatientMedications: Medication[] =
    patientMedications?.map((m, index) => ({
      id: String(index + 1),
      item: m.itemName || "",
      dose: m.dose || "",
      times: { morning: "0", midday: "0", evening: "0", night: "0" },
      quantity: m.quantity || "",
      therapyLevel: String(m.therapyLevel || "1"),
      notes: m.additionalNotes || "",
    })) || [];

  // Pagination setup - 5 items per page
  const pagination = usePagination(5);
  const { currentPage, itemsPerPage, handlePageChange, getPaginatedData } =
    pagination;

  // Calculate pagination
  const { paginatedData: paginatedMedications, totalPages } = getPaginatedData(
    DashboardPatientMedications,
  );

  return {
    DashboardPatientMedications,
    paginatedMedications,
    totalPages,
    currentPage,
    itemsPerPage,
    handlePageChange,
    patientMedications,
    patientMedicationsLoading,
    patientID,
    consultationId,
  };
};

export const savePatientMedication = async (
  medication: Medication,
  patientId: string,
  consultationId: string,
) => {
  if (!patientId) {
    console.error("No patientId provided for savePatientMedication");
    return;
  }

  try {
    const formattedMedication = {
      itemName: medication.item,
      dose: medication.dose,
      quantity: medication.quantity?.toString() || "0",
      therapyLevel: Number(medication.therapyLevel),
      additionalNotes: medication.notes,
    };

    const payload = {
      consultationId,
      patientId,
      medications: [formattedMedication],
    };

    await addPatientMedications(payload);
    await getPatientMedications(patientId);
  } catch (err) {
    console.error("Failed to save medication:", err);
  }
};

export const updatePatientMedication = async (
  index: number,
  updatedMedication: Medication,
  patientId: string,
  consultationId: string,
  existingMedications: any[],
) => {
  if (!patientId) {
    console.error(
      "Missing patientId or consultationId for updatePatientMedication",
    );
    return;
  }

  try {
    const oldMedication = existingMedications[index]; // current medication at that index

    // Map old medication -> backend format
    const formattedOld = {
      itemName: oldMedication.item ?? oldMedication.itemName,
      dose: oldMedication.dose,
      quantity: oldMedication.quantity?.toString() ?? "",
      therapyLevel: Number(oldMedication.therapyLevel),
      additionalNotes: oldMedication.notes ?? oldMedication.additionalNotes,
    };

    // Map updated medication -> backend format
    const formattedNew = {
      itemName: updatedMedication.item,
      dose: updatedMedication.dose,
      quantity: updatedMedication.quantity?.toString() ?? "",
      therapyLevel: Number(updatedMedication.therapyLevel),
      additionalNotes: updatedMedication.notes,
    };

    const payload = {
      consultationId,
      patientId,
      medications: [formattedOld, formattedNew], // backend expects [old, new]
    };

    await addPatientMedications(payload);
    await getPatientMedications(patientId);
  } catch (err) {
    console.error("Failed to update medication:", err);
  }
};
